import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radar, MapPin, Building2, ExternalLink, Star, Loader2, SlidersHorizontal, Play, Briefcase, ClipboardList, Link2, ShieldCheck, Sparkles } from "lucide-react";

const STAGE_LABEL = { exact: "your exact roles", adjacent: "adjacent roles", wider_radius: "a wider area" };

function SponsoredRolePicker({ opts, selected, setSelected }) {
  const max = opts.max_select || 10;
  const toggle = (name) => setSelected((cur) =>
    cur.includes(name) ? cur.filter((r) => r !== name) : (cur.length >= max ? cur : [...cur, name]));
  return (
    <div className="sm:col-span-2 space-y-3" data-testid="sponsored-role-picker">
      <Label className="text-xs font-semibold uppercase tracking-wider">Choose your roles ({selected.length}/{max})</Label>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="sponsored-role-disclaimer">{opts.disclaimer}</div>
      {(opts.categories || []).map((cat) => (
        <div key={cat}>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">{cat}</p>
          <div className="flex flex-wrap gap-1.5">
            {((opts.roles || {})[cat] || []).map((r) => {
              const on = selected.includes(r.name);
              const disabled = !on && selected.length >= max;
              return (
                <button type="button" key={r.id} onClick={() => toggle(r.name)} disabled={disabled} data-testid={`role-opt-${r.id}`}
                  className={`text-xs rounded-full px-3 py-1 border transition-colors ${on ? "bg-[#1a3060] text-white border-[#1a3060]" : disabled ? "bg-zinc-50 text-zinc-300 border-zinc-200 cursor-not-allowed" : "bg-white border-zinc-300 hover:border-[#1a3060]"}`}>
                  {r.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

import { ApplyAssistPanel } from "@/components/ApplyAssistPanel";
import { NhsMatchSupportingDialog } from "@/components/NhsMatchSupportingDialog";

const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const toStr = (a) => (a || []).join(", ");

const COUNTRIES = [["gb", "United Kingdom"], ["us", "United States"], ["ca", "Canada"], ["au", "Australia"], ["de", "Germany"], ["fr", "France"], ["in", "India"], ["nl", "Netherlands"], ["za", "South Africa"], ["sg", "Singapore"]];

export default function JobMatches() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [panelJob, setPanelJob] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [applying, setApplying] = useState(null);
  const [customUrl, setCustomUrl] = useState("");
  const [applyingCustom, setApplyingCustom] = useState(false);
  const [pref, setPref] = useState({ roles: "", locations: "", country: "gb", remote: "any", employment_type: "any", seniority: "any", min_salary: "", keywords_include: "", keywords_exclude: "", enabled: true });
  const [roleOpts, setRoleOpts] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const loadPrefs = async () => {
    const { data: p } = await api.get("/job-preferences");
    if (p && p.user_id) {
      setPref({
        roles: toStr(p.roles), locations: toStr(p.locations), country: p.country || "gb",
        remote: p.remote || "any", employment_type: p.employment_type || "any", seniority: p.seniority || "any",
        min_salary: p.min_salary || "", keywords_include: toStr(p.keywords_include),
        keywords_exclude: toStr(p.keywords_exclude), enabled: p.enabled !== false,
      });
      setSelectedRoles(p.roles || []);
    }
    try { const { data: ro } = await api.get("/sponsored/roles"); setRoleOpts(ro); } catch (e) { /* non-sponsored */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/job-matches");
      setData(d);
      if (!d.has_prefs) setShowPrefs(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load matches.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPrefs(); load(); }, []);

  const savePrefs = async () => {
    const rolesPayload = roleOpts?.is_sponsored ? selectedRoles : toList(pref.roles);
    if (!rolesPayload.length) { toast.error(roleOpts?.is_sponsored ? "Select at least one role from the list." : "Add at least one desired role."); return; }
    setSaving(true);
    try {
      await api.put("/job-preferences", {
        roles: rolesPayload, locations: toList(pref.locations), country: pref.country,
        remote: pref.remote, employment_type: pref.employment_type === "any" ? null : pref.employment_type,
        seniority: pref.seniority === "any" ? null : pref.seniority,
        min_salary: pref.min_salary ? parseInt(pref.min_salary) : null,
        keywords_include: toList(pref.keywords_include), keywords_exclude: toList(pref.keywords_exclude),
        enabled: pref.enabled,
      });
      toast.success("Preferences saved.");
      setShowPrefs(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save.");
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/job-matches/run-now");
      if (r.reason === "no_prefs") { toast.error("Set your job preferences first."); setShowPrefs(true); }
      else if (r.reason === "locked") toast.error("Your trial has ended — upgrade to get matches.");
      else toast.success(`Found ${r.matched} new match${r.matched === 1 ? "" : "es"}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Matching failed.");
    } finally { setRunning(false); }
  };

  const openJob = async (m) => {
    window.open(m.url, "_blank", "noopener,noreferrer");
    try { await api.post(`/job-matches/${m.id}/open`); } catch (e) { console.error("Failed to mark match opened:", e); }
    setData((d) => ({ ...d, matches: d.matches.map((x) => x.id === m.id ? { ...x, opened: true } : x) }));
  };

  const applyToMatch = (m) => {
    navigate("/apply", { state: { prefill: { job_url: m.url, job_title: m.title, company: m.company, job_description: m.snippet } } });
    api.post(`/job-matches/${m.id}/open`).catch(() => {});
  };

  const field = (k, v) => setPref((p) => ({ ...p, [k]: v }));

  const applyCustomUrl = () => {
    const url = customUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) { toast.error("Enter a full URL starting with http(s)://"); return; }
    let host = "";
    try { host = new URL(url).hostname.replace(/^www\./, ""); } catch (_) { /* ignore */ }
    navigate("/apply", { state: { prefill: { job_url: url, company: host, job_title: "" } } });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="job-matches-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Radar className="h-6 w-6 text-accent" strokeWidth={1.75} />
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Job Matches</h1>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">Fresh jobs matched to your preferences every morning. Unopened matches clear overnight.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPrefs((s) => !s)} data-testid="toggle-prefs-btn">
            <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Preferences
          </Button>
          <Button onClick={runNow} disabled={running} data-testid="run-match-now-btn">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
            {running ? "Finding jobs…" : "Run match now"}
          </Button>
        </div>
      </div>

      {roleOpts?.is_sponsored && selectedRoles.length === 0 && (
        <div className="rounded-xl border border-[#1a3060]/30 bg-[#1a3060]/5 p-4 flex items-start justify-between gap-4" data-testid="sponsored-onboarding-banner">
          <div>
            <h3 className="font-heading font-semibold text-sm text-[#1a3060] flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Welcome to the Sponsored tier</h3>
            <p className="text-xs text-muted-foreground mt-1">Pick your roles to start receiving sponsor-licensed and NHS matches. You can choose up to 10 and change them anytime.</p>
          </div>
          <Button className="shrink-0" onClick={() => setShowPrefs(true)} data-testid="onboarding-choose-roles-btn">Choose my roles</Button>
        </div>
      )}

      {data?.sponsored && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="sponsored-supply-summary">
          <p className="text-sm text-emerald-900">
            <ShieldCheck className="inline h-4 w-4 mr-1 -mt-0.5" />
            <strong>{data.sponsored.total_delivered}</strong> of your daily <strong>{data.sponsored.total_target}</strong> sponsored jobs found today
            {data.sponsored.widest_stage && data.sponsored.widest_stage !== "exact" && <span> — widened to {STAGE_LABEL[data.sponsored.widest_stage]}</span>}.
          </p>
          <p className="text-xs text-emerald-800/80 mt-1">Made up of <strong>{data.sponsored.nhs_delivered}</strong> NHS role{data.sponsored.nhs_delivered === 1 ? "" : "s"} (1–{data.sponsored.nhs_target} depending on your chosen roles) and <strong>{data.sponsored.regular_delivered}</strong> other sponsor-licensed job{data.sponsored.regular_delivered === 1 ? "" : "s"}.</p>
          <p className="text-xs text-emerald-800/80 mt-1" data-testid="sponsored-nhs-line">We only show roles at employers with a confirmed UK sponsor licence, so on low-supply days you may see fewer than {data.sponsored.total_target}. We never pad with non-sponsored jobs.</p>
          {data.sponsored.total_delivered < data.sponsored.total_target && (
            <div className="mt-2.5 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900" data-testid="supply-limited-note">
              <strong>Supply-limited today.</strong> We found {data.sponsored.total_delivered} of {data.sponsored.total_target} — sponsor-licensed openings for your roles are genuinely limited right now
              {data.sponsored.widest_stage && data.sponsored.widest_stage !== "exact" ? <span>, even after widening to {STAGE_LABEL[data.sponsored.widest_stage]}</span> : null}.
              We'd rather show fewer real sponsor jobs than pad your list. More arrive as employers post — check back tomorrow.
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="custom-url-card">
        <div className="flex items-center gap-2 mb-1.5">
          <Link2 className="h-4 w-4 text-accent" strokeWidth={1.75} />
          <h3 className="font-heading font-semibold text-sm">Apply to your own job link</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Not limited to today's matches — paste any job posting URL to apply with Apply-Assist.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder="https://…" data-testid="custom-url-input" onKeyDown={(e) => { if (e.key === "Enter") applyCustomUrl(); }} />
          <Button onClick={applyCustomUrl} disabled={applyingCustom || !customUrl.trim()} className="bg-[#24468C] hover:bg-[#1d3870] shrink-0" data-testid="custom-url-apply-btn">
            {applyingCustom ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-1.5" />} Apply
          </Button>
        </div>
      </div>

      {showPrefs && (
        <div className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4" data-testid="prefs-form">
          <div className="grid sm:grid-cols-2 gap-4">
            {roleOpts?.is_sponsored ? (
              <SponsoredRolePicker opts={roleOpts} selected={selectedRoles} setSelected={setSelectedRoles} />
            ) : (
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Desired roles (comma-separated)</Label>
              <Input value={pref.roles} onChange={(e) => field("roles", e.target.value)} placeholder="e.g. Data Analyst, Business Analyst" data-testid="pref-roles" />
            </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Locations</Label>
              <Input value={pref.locations} onChange={(e) => field("locations", e.target.value)} placeholder="e.g. London" data-testid="pref-locations" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Country</Label>
              <Select value={pref.country} onValueChange={(v) => field("country", v)}>
                <SelectTrigger data-testid="pref-country"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Work style</Label>
              <Select value={pref.remote} onValueChange={(v) => field("remote", v)}>
                <SelectTrigger data-testid="pref-remote"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="remote">Remote only</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="onsite">On-site</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Employment type</Label>
              <Select value={pref.employment_type} onValueChange={(v) => field("employment_type", v)}>
                <SelectTrigger data-testid="pref-employment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="full_time">Full-time</SelectItem>
                  <SelectItem value="part_time">Part-time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Min salary (annual)</Label>
              <Input type="number" value={pref.min_salary} onChange={(e) => field("min_salary", e.target.value)} placeholder="e.g. 40000" data-testid="pref-min-salary" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Must include keywords</Label>
              <Input value={pref.keywords_include} onChange={(e) => field("keywords_include", e.target.value)} placeholder="e.g. sql, python" data-testid="pref-include" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Exclude keywords</Label>
              <Input value={pref.keywords_exclude} onChange={(e) => field("keywords_exclude", e.target.value)} placeholder="e.g. unpaid, intern" data-testid="pref-exclude" />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={pref.enabled} onCheckedChange={(v) => field("enabled", v)} data-testid="pref-enabled" />
              Receive daily matches & morning email
            </label>
            <Button onClick={savePrefs} disabled={saving} data-testid="save-prefs-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save preferences
            </Button>
          </div>
        </div>
      )}

      {data && !data.sources?.adzuna && !data.sources?.jooble && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="sources-warning">
          Job sources aren't configured yet — matches will appear once API keys are added.
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-accent" /></div>
      ) : data && data.matches.length > 0 ? (
        <div className="space-y-3" data-testid="matches-list">
          {data.matches.map((m) => (
            <div key={m.id} className={`rounded-xl border p-4 transition-colors ${m.opened ? "border-zinc-200 bg-zinc-50" : "border-zinc-200 bg-white hover:border-accent/40"}`} data-testid="match-card">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Star className="h-4 w-4 text-gold fill-gold shrink-0" strokeWidth={1.5} />
                    <h3 className="font-heading font-semibold text-base truncate">{m.title}</h3>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-zinc-100 px-1.5 py-0.5 rounded">{m.source}</span>
                    {m.opened && <span className="text-[10px] text-muted-foreground">· opened</span>}
                    {m.sponsor?.licensed && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-800 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid="sponsor-badge">
                        <ShieldCheck className="h-3 w-3" /> Sponsor licence confirmed
                      </span>
                    )}
                    {m.job_kind === "nhs" && (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-bold text-white bg-[#005EB8] px-1.5 py-0.5 rounded" data-testid="nhs-badge">NHS</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {m.company || "—"}</span>
                    {m.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {m.location}</span>}
                    {m.salary && <span className="text-green-700 font-medium">{m.salary}</span>}
                  </div>
                  {m.sponsor?.licensed && (
                    <p className="mt-2 text-[11px] leading-snug text-emerald-900/80 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1.5" data-testid="sponsor-disclaimer">
                      {m.sponsor.disclaimer}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button size="sm" onClick={() => applyToMatch(m)} disabled={applying === m.id} className="bg-[#24468C] hover:bg-[#1d3870]" data-testid="apply-match-btn">
                    {applying === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Apply</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openJob(m)} data-testid="open-job-btn">
                    View <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                  </Button>
                  {m.job_kind === "nhs" && (
                    <NhsMatchSupportingDialog match={m} isPro={!!data?.sponsored} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-10 text-center" data-testid="matches-empty">
          <Briefcase className="h-10 w-10 mx-auto text-zinc-300 mb-3" strokeWidth={1.5} />
          <p className="font-medium">No matches yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">{data?.has_prefs ? "Tap \u201cRun match now\u201d to fetch today's jobs, or wait for your morning email." : "Set your job preferences to start receiving matched jobs."}</p>
          <Button onClick={() => (data?.has_prefs ? runNow() : setShowPrefs(true))} disabled={running} data-testid="empty-cta-btn">
            {data?.has_prefs ? "Run match now" : "Set preferences"}
          </Button>
        </div>
      )}
      <ApplyAssistPanel open={panelOpen} onOpenChange={setPanelOpen} job={panelJob} onApplied={() => toast.success("Tracked in your Application Tracker.")} />
    </div>
  );
}
